import React from 'react';

import { useGoogleApi } from './GoogleProvider';
import { useLocalStorageState } from '@gogame/hooks-ts';
let _i = 0;

const oauthEndpointURL = 'https://oauth2.googleapis.com/tokeninfo?id_token=';

const LC_GAD = 'GoogleAuthData';
const LC_ATC = 'GoogleAuthCredentials';

function getGoogleToken({ credential }) {
  return fetch(`${oauthEndpointURL}${credential}`).then((resp) => {
    if (resp?.status === 200 && resp?.json) {
      return resp.json();
    } else {
      throw new Error('Something went wrong');
    }
  });
}

export const useGoogleToken = ({
  onError,
  onSuccess,
  onNoSession,
  loginOnLoad,
  setCurrentProvider,
}) => {
  const { loaded, clientId, setReady, ready } = useGoogleApi();
  const [id] = React.useState(() => `use_login_${_i++}`);
  const [token, setToken] = useLocalStorageState(LC_GAD);
  const [credentials, setCredentials] = useLocalStorageState(LC_ATC);
  const intervalRef = React.useRef(null);

  React.useEffect(() => {
    if (token) {
      clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        try {
          const remainingSeconds = new Date(token.exp * 1000) - Date.now();

          if (remainingSeconds <= 5 * 60 * 1000) {
            window.google.accounts.id.prompt();
            // renew
          }
        } catch (e) {}
      }, 10 * 1000);
    } else {
      clearInterval(intervalRef.current);
    }
  }, [token]);

  React.useEffect(() => {
    if (!window.google || !loaded) {
      return;
    }

    if (!ready) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        auto_select: true,
        cancel_on_tap_outside: true,
        callback: async (data) => {
          try {
            const resp = await getGoogleToken({ credential: data.credential });

            setCurrentProvider();
            onSuccess(data);
            setToken(resp);
            setCredentials(data);
          } catch (error) {
            if (onError) {
              onError(error);
            }
          }
        },
      });
      if (token) {
        onSuccess(credentials);
      }
      setReady(true);
    }

    if (ready && loginOnLoad && !token && !credentials) {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          onNoSession();
        }
      });
    }
  }, [loaded, ready, id, loginOnLoad, token]);

  return {
    loaded,
    isAuthenticated: loaded && ready && !!credentials && !!token,
    token,
    signOut: () => {
      window.google.accounts.id.disableAutoSelect();
      setToken(null);
      setCredentials(null);
    },
  };
};
